import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

// Helper to get localized URL
const getLocalizedUrl = (path, lang) => {
  if (lang === 'en') return path;
  return `/${lang}${path}`;
};

// Helper to get current path without language prefix
const getPathWithoutLang = (pathname) => {
  const langPrefixes = ['/de', '/pl', '/sv', '/cs'];
  for (const prefix of langPrefixes) {
    if (pathname.startsWith(prefix)) {
      return pathname.slice(prefix.length) || '/';
    }
  }
  return pathname;
};

const Navbar = () => {
  const { t, language, setLanguage, availableLanguages, languageNames } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = getPathWithoutLang(location.pathname);
  const isHome = currentPath === '/';
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLangMenuOpen(false);
  }, [location]);

  if (isAdmin) return null;

  const navLinks = [
    { to: getLocalizedUrl('/', language), label: t('nav.home') },
    { to: getLocalizedUrl('/catalog', language), label: t('nav.catalog') },
    { to: getLocalizedUrl('/contact', language), label: t('nav.contact') },
  ];

  // Handle language change - navigate to new localized URL
  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    const newPath = getLocalizedUrl(currentPath, newLang);
    navigate(newPath);
    setIsLangMenuOpen(false);
  };

  const navBg = isScrolled || !isHome
    ? 'bg-white/90 backdrop-blur-2xl border-b border-gray-200/50'
    : 'bg-transparent';

  const textColor = isScrolled || !isHome ? 'text-night' : 'text-white';
  const logoColor = isScrolled || !isHome ? 'text-night' : 'text-white';

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-32 md:h-40">
            {/* Logo */}
            <Link 
              to="/" 
              className="transition-all duration-300 flex items-center justify-center"
              data-testid="nav-logo"
            >
              <div className="flex flex-col items-center justify-center">
                <img 
                  src="/logo-villa.png" 
                  alt="Villa.ooo" 
                  className="h-28 md:h-36 object-contain"
                />
                <span className="text-[10px] md:text-[12px] tracking-[0.3em] uppercase text-center whitespace-nowrap -mt-8" style={{color: '#1e3a5f'}}>
                  Luxury Seafront
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm tracking-wide ${textColor} hover:text-gold transition-colors duration-300`}
                  data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Language Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className={`flex items-center gap-2 text-sm ${textColor} hover:text-gold transition-colors duration-300`}
                  data-testid="language-toggle"
                >
                  <span className="uppercase font-medium">{language}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {isLangMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full right-0 mt-2 bg-white shadow-lg border border-gray-200 min-w-[140px]"
                    >
                      {availableLanguages.map((lang) => (
                        <button
                          key={lang}
                          onClick={() => handleLanguageChange(lang)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gold/10 transition-colors ${
                            language === lang ? 'text-gold font-medium' : 'text-night'
                          }`}
                          data-testid={`lang-option-${lang}`}
                        >
                          {languageNames[lang]}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden ${textColor}`}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-night pt-20"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.to}
                    className="text-3xl font-heading text-white hover:text-gold transition-colors"
                    data-testid={`mobile-nav-link-${link.to.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              
              {/* Mobile Language Selector */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap justify-center gap-4 mt-8"
              >
                {availableLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => {
                      handleLanguageChange(lang);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`px-4 py-2 text-sm uppercase tracking-wider ${
                      language === lang 
                        ? 'bg-gold text-night' 
                        : 'border border-white/30 text-white hover:border-gold hover:text-gold'
                    } transition-colors`}
                    data-testid={`mobile-lang-${lang}`}
                  >
                    {lang}
                  </button>
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
